import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { useWorkspaceObjectives } from '@/lib/workspace-objectives';
import { localIsoDate } from '@/lib/objective-progress';
import type { WorkspaceObjective } from '@shared/types/workspace-objectives';
import { measureDraftFrom, ObjectiveMeasureFields, toMeasureInput } from './ObjectiveMeasureFields';

export const ObjectiveForm = (
  { objective, onClose }: { objective?: WorkspaceObjective; onClose: () => void },
) => {
  const { objectives, userId, members, isManager, measures, save, saveMeasure } = useWorkspaceObjectives();
  const [title, setTitle] = useState(objective?.title ?? '');
  const [criteria, setCriteria] = useState(objective?.success_criteria ?? '');
  const [scope, setScope] = useState<'individual' | 'company'>(objective?.scope ?? 'individual');
  const [owner, setOwner] = useState(objective?.owner_user_id ?? userId);
  const [due, setDue] = useState(objective?.due_date ?? '');
  const [measure, setMeasure] = useState(() => measureDraftFrom(
    objective ? measures[objective.id] : undefined,
    objective ? localIsoDate(new Date(objective.created_at)) : localIsoDate(new Date()),
  ));
  const [saving, setSaving] = useState(false);
  // A new objective saved before its measure failed is updated, not created twice.
  const [savedId, setSavedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fieldClass =
    'mt-1.5 w-full rounded-lg border border-domino-cardBorder bg-domino-white px-3 py-2.5 text-sm text-domino-ink outline-none focus:border-domino-prune focus:ring-2 focus:ring-domino-lilac';
  return (
    <form
      className='mb-7 rounded-2xl border border-domino-cardBorder bg-domino-white p-5 sm:p-6'
      onSubmit={async (event) => {
        event.preventDefault();
        const measureInput = toMeasureInput(measure);
        if (typeof measureInput === 'string') {
          setError(measureInput);
          return;
        }
        setSaving(true);
        setError(null);
        try {
          const existing = objective ?? objectives.find((goal) => goal.id === savedId);
          const objectiveId = await save({
            title,
            success_criteria: criteria,
            scope,
            owner_user_id: scope === 'company' ? null : owner,
            due_date: due || null,
          }, existing);
          setSavedId(objectiveId);
          await saveMeasure(objectiveId, measureInput);
          onClose();
        } catch (cause) {
          setError(cause instanceof Error ? cause.message : 'Impossible d’enregistrer.');
        } finally {
          setSaving(false);
        }
      }}
    >
      <h2 className='mb-5 text-lg font-semibold text-domino-ink'>
        {objective ? 'Modifier l’objectif' : 'Définir un objectif'}
      </h2>
      <div className='space-y-4'>
        <label className='block text-sm font-medium text-domino-ink'>
          Résultat à atteindre<input
            autoFocus
            required
            maxLength={180}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            className={fieldClass}
          />
        </label>
        <label className='block text-sm font-medium text-domino-ink'>
          Comment reconnaître la réussite ?<textarea
            required
            rows={3}
            maxLength={1500}
            value={criteria}
            onChange={(event) => setCriteria(event.target.value)}
            className={fieldClass}
          />
        </label>
        <div className='grid gap-4 sm:grid-cols-2'>
          {isManager && !objective && (
            <label className='text-sm font-medium text-domino-ink'>
              Portée<select
                value={scope}
                onChange={(event) => setScope(event.target.value as 'individual' | 'company')}
                className={fieldClass}
              >
                <option value='individual'>Individuelle</option>
                <option value='company'>Entreprise</option>
              </select>
            </label>
          )}
          {scope === 'individual' && isManager && !objective && (
            <label className='text-sm font-medium text-domino-ink'>
              Responsable<select
                value={owner}
                onChange={(event) => setOwner(event.target.value)}
                className={fieldClass}
              >
                {members.map((member) => (
                  <option key={member.user_id} value={member.user_id}>
                    {member.user_id === userId ? 'Moi' : member.display_name || 'Membre sans nom'}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className='text-sm font-medium text-domino-ink'>
            Échéance, si connue<input
              type='date'
              value={due}
              onChange={(event) => setDue(event.target.value)}
              className={fieldClass}
            />
          </label>
        </div>
        <ObjectiveMeasureFields
          draft={measure}
          onChange={setMeasure}
          fieldClass={fieldClass}
        />
        <p className='text-xs leading-5 text-domino-inkSoft'>
          {scope === 'company'
            ? 'Visible dans toute l’entreprise. Le management peut le modifier.'
            : 'Visible par le responsable et le management. Un objectif assigné reste modifiable par le management.'}
        </p>
        {error && <p role='alert' className='text-sm text-red-700'>{error}</p>}
        <div className='flex justify-end gap-2'>
          <Button type='button' variant='ghost' onClick={onClose} disabled={saving}>Annuler</Button>
          <Button type='submit' loading={saving} disabled={!title.trim() || !criteria.trim()}>
            Enregistrer
          </Button>
        </div>
      </div>
    </form>
  );
};
